import { useState, useMemo, useEffect } from 'react';
import { Search as SearchIcon } from 'lucide-react';
import { formatUnits } from 'viem';
import { getNetworkDisplayName, NetworkType } from '@mls-wallet/shared-types';
import {
  useTransactionBuilderStore,
  TransactionBuilderPageType,
} from '../../components/transaction-builder/useTransactionBuilderStore';
import { TransactionBuilderHeader } from '../../components/transaction-builder/TransactionBuilderHeader';
import { TransactionBuilderFooter } from '../../components/transaction-builder/TransactionBuilderFooter';
import { TransactionBuilderNetworkCard } from '../../components/transaction-builder/TransactionBuilderNetworkCard';
import { useAccountAssets } from '@/hooks/account/useAccountAssets';
import { useAccountTokenUsdPrices } from '@/hooks/account/useAccountTokenUsdPrices';

/**
 * Network selection page for the transaction builder
 * Displays available networks for the selected account with search functionality
 * Shows the actual account USD value on each network by summing all asset values
 */
export function TransactionBuilderNetworkSelectionPage(): React.ReactElement {
  const account = useTransactionBuilderStore((state) => state.account);
  const { assets, isLoading: isAssetsLoading } = useAccountAssets(
    account?.id || ''
  );
  const { tokenUsdPrices, isLoading: isTokenPricesLoading } =
    useAccountTokenUsdPrices(account?.id || '');
  const setNetwork = useTransactionBuilderStore((state) => state.setNetwork);
  const navigateForwardToPage = useTransactionBuilderStore(
    (state) => state.navigateForwardToPage
  );

  const [searchQuery, setSearchQuery] = useState('');
  const isUsdDataLoading = isAssetsLoading || isTokenPricesLoading;

  // Reset network when the page loads
  // This is to ensure that the header display is correct
  useEffect(() => {
    setNetwork(undefined);
  }, [setNetwork]);

  /**
   * Calculate USD value per network based on account assets
   * Returns a Map of networkId -> total USD value
   */
  const networkValueMap = useMemo(() => {
    const valueMap = new Map<NetworkType, number>();

    if (isUsdDataLoading || !assets || assets.length === 0) {
      return valueMap;
    }

    const priceByTokenId = new Map(
      tokenUsdPrices.map((tokenUsdPrice) => [
        tokenUsdPrice.contractTokenId,
        Number(tokenUsdPrice.realUsdValue?.price ?? NaN),
      ])
    );

    for (const asset of assets) {
      const tokenPrice = priceByTokenId.get(asset.id) ?? NaN;

      for (const balance of asset.balancesByNetwork) {
        const networkId = balance.network;
        const tokenAmount = formatUnits(
          BigInt(balance.balance),
          asset.decimals
        );
        const tokenAmountNum = parseFloat(tokenAmount);
        const usdValue =
          isNaN(tokenAmountNum) || !Number.isFinite(tokenPrice)
            ? 0
            : tokenAmountNum * tokenPrice;

        // Add to existing network value or initialize
        const currentValue = valueMap.get(networkId) || 0;
        valueMap.set(networkId, currentValue + usdValue);
      }
    }

    return valueMap;
  }, [assets, isUsdDataLoading, tokenUsdPrices]);

  /**
   * Filter networks based on search query matching network name
   */
  const filteredNetworks = useMemo(() => {
    // Case: no account, return empty array
    if (!account?.networks) {
      return [];
    }

    // Case: no search query, return all networks
    if (!searchQuery.trim()) {
      return account.networks;
    }

    const queryLowerCase = searchQuery.toLowerCase();
    return account.networks.filter((networkId) => {
      const networkName = getNetworkDisplayName(networkId);
      return networkName.toLowerCase().includes(queryLowerCase);
    });
  }, [account?.networks, searchQuery]);

  const handleNetworkSelect = (network: NetworkType) => {
    setNetwork(network);
    navigateForwardToPage({
      page: TransactionBuilderPageType.CART_PAGE,
    });
  };

  return (
    <>
      <TransactionBuilderHeader
        title="Which blockchain network do you want to send your transaction on?"
        showBackButton={true}
      />
      <div className="flex flex-col w-full max-w-4xl mx-auto px-4 py-6 gap-[30px]">
        {/* Search bar */}
        <div className="bg-neutral-50 dark:bg-neutral-900 border border-white dark:border-zinc-800 rounded-full p-[5px] shadow-[0px_4px_6px_-3px_rgba(0,0,0,0.1)] my-[10px]">
          <div className="bg-white dark:bg-zinc-950 border border-zinc-100 dark:border-zinc-800 rounded-full px-[22px] py-[10px] flex items-center gap-[10px]">
            <SearchIcon className="w-4 h-4 text-zinc-500 dark:text-zinc-400 shrink-0" />
            <input
              type="text"
              placeholder="Search blockchain networks"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent text-[16px] text-foreground placeholder:text-zinc-500 dark:placeholder:text-zinc-400 outline-none border-none"
            />
          </div>
        </div>

        {/* Network list */}
        <div className="flex flex-col gap-[15px]">
          {filteredNetworks.map((networkId) => (
            <TransactionBuilderNetworkCard
              key={networkId}
              networkId={networkId}
              usdValue={networkValueMap.get(networkId) || 0}
              isLoading={isUsdDataLoading}
              onClick={() => handleNetworkSelect(networkId)}
            />
          ))}
          {filteredNetworks.length === 0 && (
            <div className="text-center text-zinc-500 dark:text-zinc-400 py-8">
              No blockchain networks found matching "{searchQuery}"
            </div>
          )}
        </div>
      </div>
      <TransactionBuilderFooter showContinueButton={false} />
    </>
  );
}
